import { useCallback, useEffect, useState } from 'react';
import { vertriebAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  BarChart3, RefreshCw, Loader2, CalendarClock, UserCheck,
  Handshake, Users, PhoneCall, UserMinus, TrendingUp,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/* Vertriebszahlen — die Wochenauswertung (Paket P3 aus dem Setter-Closer-Playbook).
 *
 * Kohortenlogik: eine Woche umfasst die Leads, die in dieser Woche EINGEGANGEN
 * sind, und zählt, was bis heute aus ihnen wurde. Junge Wochen sehen darum
 * immer schwächer aus — ihre Leads sind noch unterwegs.
 *
 * Oben die fünf Playbook-Kennzahlen mit Ziel-Ampel, darunter der Trichter je
 * Woche und je Quelle, unten die Gründe aus den Abschlussgesprächen.
 */

const GRUND_LABEL = {
  preis: 'Preis', bedenkzeit: 'Bedenkzeit', partner_fragen: 'Partner fragen',
  zeit: 'Keine Zeit', anderes_angebot: 'Anderes Angebot',
  gesundheit: 'Gesundheitlich', sonstiges: 'Sonstiges',
};

function fmtQuote(v) { return v == null ? '—' : `${v.toLocaleString('de-DE')} %`; }

/* Ziel-Ampel: grün = Ziel erreicht, amber = dran, ohne Wert = grau. */
function zielFarbe(wert, ziel, kleinerIstBesser = false) {
  if (wert == null) return 'text-muted-foreground';
  const gut = kleinerIstBesser ? wert <= ziel : wert >= ziel;
  return gut ? 'text-emerald-600' : 'text-amber-600';
}

export default function VertriebKennzahlenPage() {
  const [daten, setDaten] = useState(null);
  const [wochen, setWochen] = useState(8);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async (w) => {
    setLaedt(true);
    try {
      const res = await vertriebAPI.kennzahlen(w);
      setDaten(res.data);
    } catch {
      toast.error('Kennzahlen konnten nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(wochen); }, [laden, wochen]);

  if (laedt && !daten) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!daten) return null;
  const g = daten.gesamt;
  const z = daten.ziele;

  // Betreiber-Entscheidung 18.09.2026: sieben Zaehler statt der fuenf Quoten,
  // kein Umsatz auf dieser Seite. Die Quoten stehen weiter im Trichter.
  const c = daten.zaehler || {};
  const KACHELN = [
    { icon: Users, label: 'Leads', wert: c.leads ?? g.leads, ziel: 'neu im Zeitraum' },
    { icon: CalendarClock, label: 'Probetraining', wert: c.probetraining_gebucht ?? g.terminiert, ziel: `${c.probetraining_erschienen ?? g.erschienen} erschienen · ${fmtQuote(g.show_rate)}` },
    { icon: Handshake, label: 'Abschluss', wert: c.abschluesse ?? g.abschluesse, ziel: `${g.tag1 || 0}× Tag 1 · ${fmtQuote(g.abschlussquote)}` },
    { icon: PhoneCall, label: 'Follow-up', wert: c.follow_ups_erledigt ?? 0, ziel: `${c.follow_ups_offen ?? 0} offen${c.follow_ups_ueberfaellig ? ` · ${c.follow_ups_ueberfaellig} überfällig` : ''}`, farbe: c.follow_ups_ueberfaellig ? 'text-amber-600' : '' },
    { icon: UserMinus, label: 'Kündigung', wert: c.kuendigungen ?? 0, ziel: 'im Zeitraum', farbe: c.kuendigungen ? 'text-red-600' : '' },
    { icon: UserCheck, label: 'Bestandskunden', wert: c.bestandskunden ?? 0, ziel: 'aktive Mitgliedschaften' },
    { icon: TrendingUp, label: 'Upsells', wert: c.upsells?.gesamt ?? 0, ziel: `${c.upsells?.umvertragungen ?? 0} Umvertragungen · ${c.upsells?.gym_sauna ?? 0} Gym & Sauna · ${c.upsells?.checkups ?? 0} Check-Ups`, farbe: c.upsells?.gesamt ? 'text-emerald-600' : '' },
  ];

  const Trichter = ({ z: zeile }) => (
    <>
      <td className="px-3 py-2.5 text-right tabular-nums font-bold">{zeile.leads}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{zeile.kontaktiert}<span className="text-[10px] text-muted-foreground block">{zeile.speed_to_lead_schnitt != null ? `Ø ${zeile.speed_to_lead_schnitt} min` : ''}</span></td>
      <td className="px-3 py-2.5 text-right tabular-nums">{zeile.terminiert}<span className="text-[10px] text-muted-foreground block">{fmtQuote(zeile.terminquote)}</span></td>
      <td className="px-3 py-2.5 text-right tabular-nums">{zeile.erschienen}<span className="text-[10px] text-muted-foreground block">{fmtQuote(zeile.show_rate)}</span></td>
      <td className="px-3 py-2.5 text-right tabular-nums font-bold text-emerald-600">{zeile.abschluesse}<span className="text-[10px] text-muted-foreground font-normal block">{[zeile.tag1 > 0 ? `${zeile.tag1}× Tag 1` : '', zeile.abschluesse_ohne_pt > 0 ? `${zeile.abschluesse_ohne_pt} ohne Probetr.` : '', zeile.erschienen > 0 ? fmtQuote(zeile.abschlussquote) : ''].filter(Boolean).join(' · ')}</span></td>
    </>
  );

  const Balken = ({ eintraege, titel }) => {
    const max = Math.max(1, ...eintraege.map(([, n]) => n));
    return (
      <Card><CardContent className="p-4">
        <h3 className="text-sm font-bold mb-3">{titel}</h3>
        {eintraege.length === 0
          ? <p className="text-xs text-muted-foreground">Noch keine Daten — die Gründe kommen aus den Abschlussgesprächen.</p>
          : eintraege.map(([grund, n]) => (
            <div key={grund} className="flex items-center gap-2 mb-1.5">
              <span className="text-xs w-32 flex-none">{GRUND_LABEL[grund] || grund}</span>
              <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                <div className="h-full bg-marke/70 rounded" style={{ width: `${(n / max) * 100}%` }} />
              </div>
              <span className="text-xs tabular-nums w-6 text-right font-bold">{n}</span>
            </div>
          ))}
      </CardContent></Card>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="kennzahlen-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-marke" /> Vertriebszahlen</h1>
          <p className="text-sm text-muted-foreground">Leads nach Eingangswoche — gezählt wird, was bis heute aus ihnen wurde. Zeitraum {daten.zeitraum.von} bis {daten.zeitraum.bis}.</p>
        </div>
        <div className="flex items-center gap-1.5">
          {[4, 8, 12].map((w) => (
            <button key={w} onClick={() => setWochen(w)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-bold ${wochen === w ? 'border-marke text-marke bg-marke/5' : 'border-border text-muted-foreground hover:bg-muted'}`}>
              {w} Wo.
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={() => laden(wochen)}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Die sieben Kennzahlen (Betreiber 18.09.2026) */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {KACHELN.map((k) => (
          <Card key={k.label} data-testid={`vz-${k.label.toLowerCase()}`}><CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><k.icon className="w-3.5 h-3.5" /> {k.label}</div>
            <div className={`text-2xl font-extrabold tabular-nums ${k.farbe || ''}`}>{k.wert}</div>
            <div className="text-[10px] text-muted-foreground">{k.ziel}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Trichter je Woche */}
      <Card><CardContent className="p-0">
        <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
          <h2 className="text-sm font-bold">Trichter je Eingangswoche</h2>
          <span className="text-xs text-muted-foreground">junge Wochen sind naturgemäß noch unfertig</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2 text-left">Woche</th>
              <th className="px-3 py-2 text-right">Leads</th>
              <th className="px-3 py-2 text-right">Kontaktiert</th>
              <th className="px-3 py-2 text-right">Terminiert</th>
              <th className="px-3 py-2 text-right">Erschienen</th>
              <th className="px-3 py-2 text-right">Abschlüsse</th>
            </tr></thead>
            <tbody>
              {daten.wochen.map((w) => (
                <tr key={w.woche} className="border-t border-border/40">
                  <td className="px-4 py-2.5 font-mono text-xs">{w.woche}</td>
                  <Trichter z={w} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Trichter je Quelle (ohne Umsatz — Betreiber 18.09.2026) */}
      <Card><CardContent className="p-0">
        <div className="px-4 py-3 border-b border-border/60">
          <h2 className="text-sm font-bold flex items-center gap-1.5"><Users className="w-4 h-4 text-marke" /> Quellen</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2 text-left">Quelle</th>
              <th className="px-3 py-2 text-right">Leads</th>
              <th className="px-3 py-2 text-right">Kontaktiert</th>
              <th className="px-3 py-2 text-right">Terminiert</th>
              <th className="px-3 py-2 text-right">Erschienen</th>
              <th className="px-3 py-2 text-right">Abschlüsse</th>
            </tr></thead>
            <tbody>
              {daten.quellen.length === 0
                ? <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground text-sm">Noch keine Leads im Zeitraum.</td></tr>
                : daten.quellen.map((q) => (
                  <tr key={q.quelle} className="border-t border-border/40">
                    <td className="px-4 py-2.5 text-xs font-medium">{q.quelle}</td>
                    <Trichter z={q} />
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Gründe aus den Abschlussgesprächen */}
      <div className="grid md:grid-cols-2 gap-3">
        <Balken titel={'„Warum heute nicht?" — Folgetermine'} eintraege={daten.gruende.folgetermin} />
        <Balken titel="Warum verloren?" eintraege={daten.gruende.verloren} />
      </div>

      {daten.archivierte > 0 && (
        <p className="text-[11px] text-muted-foreground max-w-2xl">
          <b>{daten.archivierte}</b> Lead{daten.archivierte === 1 ? '' : 's'} aus diesem Zeitraum liegen im Archiv
          und sind in den Zahlen oben nicht enthalten. Die Angabe steht hier, damit sichtbar bleibt,
          worauf sich die Quoten beziehen.
        </p>
      )}
      <p className="text-[11px] text-muted-foreground max-w-2xl">Die Ziele (unter den Kacheln) sind die Startannahmen aus dem Playbook — nach vier Wochen echter Messung werden sie an die Realität angepasst. Wochenauswertung: eine Engpass-These, eine Maßnahme.</p>
    </div>
  );
}
